import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "load-reports";

export interface LoadReportLine {
	id: string;
	loadReportId: string;
	lineNum: number | null;
	itemCode: string;
	itemDescription: string | null;
	quantity: string;
	warehouse: string | null;
	stockItemId: string | null;
	sapDocEntry: number | null;
	sapDocNum: number | null;
}

export interface LoadReport {
	id: string;
	companyId?: string | null;
	companyName?: string | null;
	stockItemId: string | null;
	stockItemCode: string | null;
	stockItemName: string | null;
	sapDocEntry: number | null;
	sapDocNum: number | null;
	supplierCardCode?: string | null;
	supplierName?: string | null;
	reservationNo: string | null;
	shipperName?: string | null;
	departurePort: string | null;
	arrivalPort: string | null;
	bookingNo: string | null;
	hblNo: string | null;
	vesselName: string | null;
	departureDate: string;
	estimatedArrivalDate: string;
	equipmentType: string;
	status: "waiting" | "arrived" | "customs_processing" | "customs_completed" | "factory_arrived";
	notes: string | null;
	createdAt: string;
	lines: LoadReportLine[];
}

export interface CreateLoadReportInput {
	companyId?: string;
	stockItemId?: string;
	sapDocEntry?: number;
	sapDocNum?: number;
	supplierCardCode?: string;
	supplierName?: string;
	reservationNo?: string;
	shipperName?: string;
	departurePort?: string;
	arrivalPort?: string;
	bookingNo?: string;
	hblNo?: string;
	vesselName?: string;
	departureDate: string;
	estimatedArrivalDate: string;
	equipmentType: string;
	notes?: string;
	lines?: Array<{
		lineNum?: number;
		itemCode: string;
		itemDescription?: string;
		quantity: number | string;
		warehouse?: string;
		stockItemId?: string;
		sapDocEntry?: number;
		sapDocNum?: number;
	}>;
}

export function useLoadReportList(options?: {
	status?: string;
	search?: string;
	sort?: string;
	dir?: "asc" | "desc";
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.status) params.set("status", options.status);
			if (options?.search) params.set("search", options.search);
			if (options?.sort) params.set("sort", options.sort);
			if (options?.dir) params.set("dir", options.dir);
			return api.get<{ items: LoadReport[]; total: number }>(
				`/api/load-reports?${params.toString()}`,
			);
		},
	});
}

export function useCreateLoadReport() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateLoadReportInput) => api.post<LoadReport>("/api/load-reports", data),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useUpdateLoadReport() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: CreateLoadReportInput }) =>
			api.patch<LoadReport>(`/api/load-reports/${id}`, data),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useUpdateLoadReportStatus() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, status }: { id: string; status: string }) =>
			api.patch<LoadReport>(`/api/load-reports/${id}/status`, { status }),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useDeleteLoadReport() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete(`/api/load-reports/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}
